import { defineStore } from 'pinia'
import type { EmployeeIdentity, PayPeriod } from '@/types'
import { axiosGraph } from '@/plugins/axios'
import dataService from '@/data/data.service'
import { todayIso } from '@/utils/dates'

/**
 * Replaces the Vuex root store: who is signed in, the current pay period, and
 * the day being entered. Persisted to sessionStorage as before, so a reload
 * mid-entry keeps its place and a closed tab forgets it.
 */
export const useAppStore = defineStore('app', {
  state: () => ({
    employee: null as EmployeeIdentity | null,
    weekOf: null as PayPeriod | null,
    /** The day time is being entered for, `YYYY-MM-DD`. */
    dayOfWeek: todayIso(),
    /** The date chosen on the day view, `YYYY-MM-DD`. */
    dateEarned: todayIso(),
  }),

  getters: {
    employeeNumber: (state) => state.employee?.employeeNumber ?? '',
    employeeName: (state) => state.employee?.employeeName ?? '',
    /** The pay period's start date as the API sends it, trimmed to the day. */
    payPeriodDate: (state) => state.weekOf?.payPeriodDate?.slice(0, 10) ?? '',
    weekCount: (state) => state.weekOf?.weekCount ?? 0,
  },

  actions: {
    setEmployee(value: EmployeeIdentity | null) {
      this.employee = value
    },
    setWeekOf(value: PayPeriod | null) {
      this.weekOf = value
    },
    setDayOfWeek(value: string) {
      this.dayOfWeek = value
    },
    setDateEarned(value: string) {
      this.dateEarned = value
    },

    /**
     * Who is signed in, from Microsoft Graph: the directory's employeeId is the
     * payroll employee number every API call is keyed on. Fetched once per
     * session; pages await this before loading anything of their own.
     */
    async ensureIdentity(): Promise<EmployeeIdentity | null> {
      if (this.employee?.employeeNumber) return this.employee
      try {
        const { data } = await axiosGraph.get<{ employeeId?: string; displayName?: string }>(
          'v1.0/me',
          { params: { $select: 'employeeId,displayName' } },
        )
        this.employee = {
          employeeNumber: data.employeeId ?? '',
          employeeName: data.displayName ?? '',
        }
      } catch (err) {
        console.error('axiosGraphError: ', err)
      }
      return this.employee
    },

    /** The current pay period, refreshed from the API. */
    async loadPayPeriod(): Promise<PayPeriod | null> {
      const periods = await dataService.getPayPeriod()
      this.weekOf = periods[0] ?? null
      return this.weekOf
    },

    /** The current pay period, fetched only if this session has not yet. */
    async ensurePayPeriod(): Promise<PayPeriod | null> {
      if (this.weekOf?.payPeriodDate) return this.weekOf
      return this.loadPayPeriod()
    },

    reset() {
      this.employee = null
      this.weekOf = null
      this.dayOfWeek = todayIso()
      this.dateEarned = todayIso()
    },
  },

  persist: {
    storage: sessionStorage,
    pick: ['employee', 'weekOf', 'dayOfWeek', 'dateEarned'],
  },
})
